import { useEffect, useRef, useState, type ReactNode } from "react"
import { Link, NavLink } from "react-router-dom"

// A âncora da marca, desenhada aqui em vez de puxada de /assets: em produção o
// Propshaft serve o arquivo com digest no nome, e um caminho fixo daria 404.
function Ancora({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      className={className}
      fill="none"
      stroke="currentColor"
      strokeWidth={2.2}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <circle cx="12" cy="5" r="2" />
      <line x1="12" y1="7" x2="12" y2="22" />
      <path d="M5 12a7 7 0 0 0 14 0" />
      <line x1="8" y1="10" x2="16" y2="10" />
    </svg>
  )
}

// Só o painel fica à vista. Histórico mora no menu da conta: na barra, ele
// disputava atenção com a única coisa que a pessoa deveria fazer aqui, que é
// o próximo simulado.
const LINKS = [{ para: "/painel", rotulo: "Painel" }]

/**
 * Sair sem sair da SPA. Antes era um formulário escondido dentro de
 * Configurações — três passos para uma ação que todo mundo espera achar no
 * canto superior direito.
 */
async function sair() {
  const token = document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content
  try {
    await fetch("/logout", {
      method: "DELETE",
      credentials: "same-origin",
      headers: { "X-CSRF-Token": token ?? "", Accept: "application/json" },
    })
  } catch {
    // Falha de rede não deve prender ninguém na sessão: redireciona assim mesmo.
  }
  window.location.href = "/"
}

function MenuDaConta({ admin }: { admin: boolean }) {
  const [aberto, setAberto] = useState(false)
  const raiz = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!aberto) return

    function noClique(evento: MouseEvent) {
      if (raiz.current && !raiz.current.contains(evento.target as Node)) setAberto(false)
    }
    function naTecla(evento: KeyboardEvent) {
      if (evento.key === "Escape") setAberto(false)
    }

    document.addEventListener("mousedown", noClique)
    document.addEventListener("keydown", naTecla)
    return () => {
      document.removeEventListener("mousedown", noClique)
      document.removeEventListener("keydown", naTecla)
    }
  }, [aberto])

  const itemClasse = "block w-full px-4 py-2.5 text-left text-sm text-navy-800 transition-colors hover:bg-navy-50"

  return (
    <div className="relative" ref={raiz}>
      <button
        type="button"
        onClick={() => setAberto((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={aberto}
        className="inline-flex items-center gap-1 rounded-lg px-2 py-1.5 text-sm text-navy-100 transition-colors hover:text-white sm:gap-1.5 sm:px-3"
      >
        Conta
        <svg
          viewBox="0 0 20 20"
          fill="currentColor"
          className={`h-4 w-4 transition-transform ${aberto ? "rotate-180" : ""}`}
          aria-hidden="true"
        >
          <path
            fillRule="evenodd"
            d="M5.23 7.21a.75.75 0 0 1 1.06.02L10 11.06l3.71-3.83a.75.75 0 1 1 1.08 1.04l-4.25 4.39a.75.75 0 0 1-1.08 0L5.21 8.27a.75.75 0 0 1 .02-1.06Z"
            clipRule="evenodd"
          />
        </svg>
      </button>

      {aberto && (
        <div
          role="menu"
          className="absolute right-0 z-50 mt-2 w-52 overflow-hidden rounded-xl border border-navy-100 bg-white py-1 shadow-lg shadow-navy-900/15"
        >
          <Link to="/historico" role="menuitem" className={itemClasse} onClick={() => setAberto(false)}>
            Histórico
          </Link>
          <Link to="/configuracoes" role="menuitem" className={itemClasse} onClick={() => setAberto(false)}>
            Configurações
          </Link>
          <Link to="/suporte" role="menuitem" className={itemClasse} onClick={() => setAberto(false)}>
            Suporte
          </Link>
          {admin && (
            <a href="/admin" role="menuitem" className={itemClasse}>
              Admin de curadoria
            </a>
          )}
          <button
            type="button"
            role="menuitem"
            onClick={() => {
              setAberto(false)
              void sair()
            }}
            className={`${itemClasse} border-t border-navy-100 text-red-600 hover:bg-red-50`}
          >
            Sair
          </button>
        </div>
      )}
    </div>
  )
}

/** Casca das telas fora do simulado. Dentro da prova ela não existe: uma tarefa só. */
export function Casca({ admin, children }: { admin: boolean; children: ReactNode }) {
  return (
    <div className="min-h-[100dvh] bg-areia">
      <header className="sticky top-0 z-30 border-b border-navy-800 bg-navy-900">
        <div className="mx-auto flex h-14 max-w-4xl items-center justify-between px-4">
          {/* `min-w-0` + `truncate`: se um dia a navegação crescer, a marca
              encolhe com reticências em vez de empurrar o cabeçalho para fora
              da tela do celular. */}
          <Link to="/painel" className="flex min-w-0 items-center gap-2" aria-label="CarteiraNáutica — painel">
            <Ancora className="h-6 w-6 flex-none text-gold-500" />
            <span className="truncate font-display text-lg text-white">
              Carteira<span className="text-gold-500">Náutica</span>
            </span>
          </Link>

          {/* Espaçamento apertado no celular de propósito: marca + link + menu
              precisam caber em 375px sem o cabeçalho rolar para o lado. */}
          <nav className="flex items-center gap-0.5 sm:gap-1" aria-label="Navegação principal">
            {LINKS.map((l) => (
              <NavLink
                key={l.para}
                to={l.para}
                className={({ isActive }) =>
                  `rounded-lg px-2 py-1.5 text-sm transition-colors sm:px-3 ${
                    isActive ? "bg-navy-800 font-semibold text-gold-500" : "text-navy-100 hover:text-white"
                  }`
                }
              >
                {l.rotulo}
              </NavLink>
            ))}
            <MenuDaConta admin={admin} />
          </nav>
        </div>
      </header>

      <main className="mx-auto max-w-4xl px-4 pb-20 pt-6">{children}</main>
    </div>
  )
}
